import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Wrench } from "lucide-react";

/**
 * AdditionalTradesBuilder
 *
 * Alpha Adjusters / Claims-style instant-quote builder. Each trade carries its
 * own measurement fields, formula, and optional toggles. Sales rep adds per-line
 * profit + a master commission slider for the whole job.
 */

export type TradeKey =
  | "gutters"
  | "siding"
  | "windows"
  | "doors"
  | "painting"
  | "fascia_soffit"
  | "hvac"
  | "water_system"
  | "custom";

export interface TradeFieldDef {
  key: string;
  label: string;
  unit: string;
  defaultValue?: number;
  type?: "number" | "toggle";
}

export interface TradeDef {
  key: TradeKey;
  label: string;
  defaultRate: number;
  rateUnit: string;
  rateLabel?: string; // optional override for the rate field label
  fields: TradeFieldDef[];
  // Compute the materials/labor subtotal (BEFORE per-line profit margin).
  computeSubtotal: (fields: Record<string, number>, rate: number) => number;
  helper?: string;
}

const TRADE_DEFS: TradeDef[] = [
  {
    key: "gutters",
    label: "Gutters (LF × 1.15 + Fascia/Soffit)",
    defaultRate: 15, // 5" default $15/LF; 6" = $20/LF
    rateUnit: "LF",
    rateLabel: "Gutter $/LF (5\"=$15, 6\"=$20)",
    helper: "Gutter LF × 1.15 waste × $/LF, plus fascia/soffit LF and downspouts. Toggle gutter guard for the all-in-one package.",
    fields: [
      { key: "gutterLf", label: "Gutter LF", unit: "LF", defaultValue: 0 },
      { key: "fasciaLf", label: "Fascia LF", unit: "LF", defaultValue: 0 },
      { key: "soffitLf", label: "Soffit LF", unit: "LF", defaultValue: 0 },
      { key: "fasciaSoffitRate", label: "Fascia/Soffit $/LF", unit: "$", defaultValue: 14 },
      { key: "downspouts", label: "# Downspouts", unit: "ea", defaultValue: 0 },
      { key: "downspoutRate", label: "Downspout $/ea", unit: "$", defaultValue: 75 },
      { key: "guard", label: "Add Gutter Guard", unit: "", defaultValue: 0, type: "toggle" },
      { key: "guardRate", label: "Gutter Guard $/LF", unit: "$", defaultValue: 8 },
    ],
    computeSubtotal: (f, rate) => {
      const gutter = (f.gutterLf || 0) * 1.15 * (rate || 0);
      const fasciaSoffit =
        ((f.fasciaLf || 0) + (f.soffitLf || 0)) * (f.fasciaSoffitRate || 0);
      const downs = (f.downspouts || 0) * (f.downspoutRate || 0);
      const guard = f.guard ? (f.gutterLf || 0) * (f.guardRate || 0) : 0;
      return gutter + fasciaSoffit + downs + guard;
    },
  },
  {
    key: "windows",
    label: "Windows (by United Inch)",
    defaultRate: 12, // $/united inch
    rateUnit: "UI",
    rateLabel: "$ per United Inch",
    helper: "United Inch = width + height. Subtotal = qty × UI × rate. Toggle grates to add the per-window upcharge.",
    fields: [
      { key: "qty", label: "# Windows", unit: "ea", defaultValue: 0 },
      { key: "widthIn", label: "Width (in)", unit: "in", defaultValue: 36 },
      { key: "heightIn", label: "Height (in)", unit: "in", defaultValue: 48 },
      { key: "grates", label: "With Grates", unit: "", defaultValue: 0, type: "toggle" },
      { key: "grateUpcharge", label: "Grate $/window", unit: "$", defaultValue: 85 },
    ],
    computeSubtotal: (f, rate) => {
      const ui = (f.widthIn || 0) + (f.heightIn || 0);
      const base = (f.qty || 0) * ui * (rate || 0);
      const grateAdd = f.grates ? (f.qty || 0) * (f.grateUpcharge || 0) : 0;
      return base + grateAdd;
    },
  },
  {
    key: "hvac",
    label: "HVAC (per Ton Unit)",
    defaultRate: 0, // not used – each tonnage has its own price
    rateUnit: "UNIT",
    helper: "Add the # of units at each tonnage and the price per unit. 2-ton, 3-ton, 4-ton and 5-ton each priced separately.",
    fields: [
      { key: "qty2", label: "# 2-Ton Units", unit: "ea", defaultValue: 0 },
      { key: "price2", label: "2-Ton Price ea", unit: "$", defaultValue: 4500 },
      { key: "qty3", label: "# 3-Ton Units", unit: "ea", defaultValue: 0 },
      { key: "price3", label: "3-Ton Price ea", unit: "$", defaultValue: 5400 },
      { key: "qty4", label: "# 4-Ton Units", unit: "ea", defaultValue: 0 },
      { key: "price4", label: "4-Ton Price ea", unit: "$", defaultValue: 6800 },
      { key: "qty5", label: "# 5-Ton Units", unit: "ea", defaultValue: 0 },
      { key: "price5", label: "5-Ton Price ea", unit: "$", defaultValue: 8000 },
    ],
    computeSubtotal: (f) =>
      (f.qty2 || 0) * (f.price2 || 0) +
      (f.qty3 || 0) * (f.price3 || 0) +
      (f.qty4 || 0) * (f.price4 || 0) +
      (f.qty5 || 0) * (f.price5 || 0),
  },
  {
    key: "water_system",
    label: "Water System (lump cost)",
    defaultRate: 0,
    rateUnit: "JOB",
    rateLabel: "Cost",
    helper: "Enter the water system / softener / heater cost as a lump sum.",
    fields: [
      { key: "cost", label: "Water System Cost", unit: "$", defaultValue: 0 },
    ],
    computeSubtotal: (f) => f.cost || 0,
  },
  {
    key: "siding",
    label: "Siding",
    defaultRate: 9,
    rateUnit: "SQ FT",
    fields: [
      { key: "wallSqFt", label: "Wall Sq Ft", unit: "SF", defaultValue: 0 },
      { key: "openingsSqFt", label: "Openings Sq Ft (deduct)", unit: "SF", defaultValue: 0 },
    ],
    computeSubtotal: (f, rate) =>
      Math.max(0, (f.wallSqFt || 0) - (f.openingsSqFt || 0)) * (rate || 0),
  },
  {
    key: "doors",
    label: "Doors",
    defaultRate: 1200,
    rateUnit: "EA",
    fields: [
      { key: "qty", label: "# Doors", unit: "ea", defaultValue: 0 },
    ],
    computeSubtotal: (f, rate) => (f.qty || 0) * (rate || 0),
  },
  {
    key: "painting",
    label: "Painting",
    defaultRate: 4,
    rateUnit: "SQ FT",
    fields: [
      { key: "areaSqFt", label: "Paint Area Sq Ft", unit: "SF", defaultValue: 0 },
    ],
    computeSubtotal: (f, rate) => (f.areaSqFt || 0) * (rate || 0),
  },
  {
    key: "fascia_soffit",
    label: "Fascia / Soffit (standalone)",
    defaultRate: 14,
    rateUnit: "LF",
    fields: [
      { key: "fasciaLf", label: "Fascia LF", unit: "LF", defaultValue: 0 },
      { key: "soffitLf", label: "Soffit LF", unit: "LF", defaultValue: 0 },
    ],
    computeSubtotal: (f, rate) =>
      ((f.fasciaLf || 0) + (f.soffitLf || 0)) * (rate || 0),
  },
  {
    key: "custom",
    label: "Custom Trade",
    defaultRate: 0,
    rateUnit: "EA",
    fields: [
      { key: "qty", label: "Quantity", unit: "", defaultValue: 0 },
    ],
    computeSubtotal: (f, rate) => (f.qty || 0) * (rate || 0),
  },
];

export interface TradeLine {
  id: string;
  tradeKey: TradeKey;
  customName?: string;
  customUnit?: string;
  rate: number;
  fields: Record<string, number>;
  profitDollars: number;
}

export interface AdditionalTradesBuilderProps {
  baseQuoteTotal: number;
  onChange?: (data: {
    lines: TradeLine[];
    tradesSubtotal: number;
    commissionAmount: number;
    grandTotal: number;
  }) => void;
}

const formatMoney = (n: number) =>
  `$${Math.round(n).toLocaleString("en-US")}`;

function getTradeDef(key: TradeKey): TradeDef {
  return TRADE_DEFS.find((t) => t.key === key) ?? TRADE_DEFS[TRADE_DEFS.length - 1];
}

const PROFIT_RANGES: Record<TradeKey, { max: number; step: number }> = {
  gutters: { max: 5000, step: 1 },
  siding: { max: 25000, step: 1 },
  windows: { max: 25000, step: 1 },
  doors: { max: 10000, step: 1 },
  painting: { max: 15000, step: 1 },
  fascia_soffit: { max: 5000, step: 1 },
  hvac: { max: 15000, step: 1 },
  water_system: { max: 10000, step: 1 },
  custom: { max: 25000, step: 1 },
};

function computeLineSubtotal(line: TradeLine): number {
  const def = getTradeDef(line.tradeKey);
  return def.computeSubtotal(line.fields, line.rate);
}

function computeLineTotal(line: TradeLine): number {
  return computeLineSubtotal(line) + (line.profitDollars || 0);
}

export function AdditionalTradesBuilder({
  baseQuoteTotal,
  onChange,
}: AdditionalTradesBuilderProps) {
  const [lines, setLines] = useState<TradeLine[]>([]);
  const [pickerKey, setPickerKey] = useState<TradeKey | "">("");
  const [commissionDollars, setCommissionDollars] = useState(0);

  const addTrade = () => {
    if (!pickerKey) return;
    const def = getTradeDef(pickerKey);
    const fields: Record<string, number> = {};
    def.fields.forEach((f) => {
      fields[f.key] = f.defaultValue ?? 0;
    });
    const newLine: TradeLine = {
      id: crypto.randomUUID(),
      tradeKey: pickerKey,
      rate: def.defaultRate,
      fields,
      profitDollars: 0,
      ...(pickerKey === "custom" ? { customName: "", customUnit: "EA" } : {}),
    };
    setLines((prev) => [...prev, newLine]);
    setPickerKey("");
  };

  const removeLine = (id: string) =>
    setLines((prev) => prev.filter((l) => l.id !== id));

  const updateLine = (id: string, patch: Partial<TradeLine>) =>
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  const updateLineField = (id: string, fieldKey: string, value: number) =>
    setLines((prev) =>
      prev.map((l) =>
        l.id === id ? { ...l, fields: { ...l.fields, [fieldKey]: value } } : l
      )
    );

  const tradesSubtotal = useMemo(
    () => lines.reduce((sum, l) => sum + computeLineTotal(l), 0),
    [lines]
  );

  const grandTotal = baseQuoteTotal + tradesSubtotal + commissionDollars;

  useMemo(() => {
    onChange?.({
      lines,
      tradesSubtotal,
      commissionAmount: commissionDollars,
      grandTotal,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, tradesSubtotal, commissionDollars, grandTotal]);

  return (
    <div className="min-w-0 rounded-xl border border-border bg-card p-4 space-y-4 overflow-hidden">
      <div className="flex items-center gap-2">
        <Wrench className="w-4 h-4 text-primary" />
        <h3 className="font-semibold">Additional Trades</h3>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        Stack trades on top of the roof. Each trade has its own measurement formula
        — windows by united inch, HVAC by ton, gutters by LF × 1.15, water systems
        as lump sum. Add per-line profit, then commission below.
      </p>

      {/* Picker */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Select value={pickerKey} onValueChange={(v: TradeKey) => setPickerKey(v)}>
          <SelectTrigger className="w-full flex-1 min-w-0">
            <SelectValue placeholder="Choose a trade to add…" />
          </SelectTrigger>
          <SelectContent className="bg-popover z-50">
            {TRADE_DEFS.map((t) => (
              <SelectItem key={t.key} value={t.key}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          onClick={addTrade}
          disabled={!pickerKey}
          className="w-full gap-1 sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          Add
        </Button>
      </div>

      {/* Trade lines */}
      {lines.length > 0 && (
        <div className="space-y-3">
          {lines.map((line) => {
            const def = getTradeDef(line.tradeKey);
            return (
              <div
                key={line.id}
                className="rounded-lg border border-border bg-background p-3 space-y-3"
              >
                <div className="flex items-center justify-between gap-2">
                  {line.tradeKey === "custom" ? (
                    <Input
                      value={line.customName ?? ""}
                      onChange={(e) =>
                        updateLine(line.id, { customName: e.target.value.slice(0, 60) })
                      }
                      placeholder="Trade name"
                      className="h-8 w-full text-sm font-medium sm:max-w-[180px]"
                    />
                  ) : (
                    <div className="min-w-0 text-sm font-semibold break-words pr-2">{def.label}</div>
                  )}
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => removeLine(line.id)}
                    aria-label="Remove trade"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {def.helper && (
                  <p className="text-[11px] leading-4 text-muted-foreground">{def.helper}</p>
                )}

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {def.fields.map((f) => {
                    if (f.type === "toggle") {
                      return (
                        <div
                          key={f.key}
                          className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-2 py-1.5 sm:col-span-2"
                        >
                          <Label className="text-xs font-medium">{f.label}</Label>
                          <Switch
                            checked={!!line.fields[f.key]}
                            onCheckedChange={(checked) =>
                              updateLineField(line.id, f.key, checked ? 1 : 0)
                            }
                          />
                        </div>
                      );
                    }
                    return (
                      <div key={f.key} className="space-y-1">
                        <Label className="text-[11px] leading-4 text-muted-foreground uppercase tracking-wide">
                          {f.label}
                        </Label>
                        <Input
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step="0.01"
                          value={line.fields[f.key] ?? 0}
                          onChange={(e) =>
                            updateLineField(
                              line.id,
                              f.key,
                              Math.max(0, Number(e.target.value) || 0)
                            )
                          }
                          className="h-8 text-sm"
                        />
                      </div>
                    );
                  })}
                  {def.key !== "water_system" && def.key !== "hvac" && (
                    <div className="space-y-1">
                      <Label className="text-[11px] leading-4 text-muted-foreground uppercase tracking-wide break-words">
                        {def.rateLabel ??
                          `Rate ($/${line.tradeKey === "custom" ? (line.customUnit || "ea") : def.rateUnit})`}
                      </Label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        value={line.rate}
                        onChange={(e) =>
                          updateLine(line.id, {
                            rate: Math.max(0, Number(e.target.value) || 0),
                          })
                        }
                        className="h-8 text-sm"
                      />
                    </div>
                  )}
                  {line.tradeKey === "custom" && (
                    <div className="space-y-1">
                      <Label className="text-[11px] leading-4 text-muted-foreground uppercase tracking-wide">
                        Unit
                      </Label>
                      <Input
                        value={line.customUnit ?? ""}
                        onChange={(e) =>
                          updateLine(line.id, {
                            customUnit: e.target.value.slice(0, 10).toUpperCase(),
                          })
                        }
                        placeholder="EA / LF / SF"
                        className="h-8 text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Per-trade profit margin (dollars) */}
                {(() => {
                  const range = PROFIT_RANGES[line.tradeKey] ?? { max: 15000, step: 1 };
                  return (
                    <div className="space-y-2 pt-2 border-t border-border">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">
                          Profit / Commission for this trade
                        </Label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                            $
                          </span>
                          <Input
                            type="text"
                            inputMode="numeric"
                            value={line.profitDollars}
                            onChange={(e) => {
                              const cleaned = e.target.value.replace(/[^\d]/g, "");
                              const n = Math.max(0, Math.min(range.max, Number(cleaned) || 0));
                              updateLine(line.id, { profitDollars: n });
                            }}
                            className="h-7 w-24 pl-5 text-right text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </div>
                      </div>
                      <Slider
                        value={[line.profitDollars]}
                        onValueChange={(v) => updateLine(line.id, { profitDollars: v[0] })}
                        min={0}
                        max={range.max}
                        step={range.step}
                        className="w-full"
                      />
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>$0</span>
                        <span>{formatMoney(range.max)}</span>
                      </div>
                    </div>
                  );
                })()}

                <div className="flex justify-between items-center pt-1 border-t border-border text-xs">
                  <span className="text-muted-foreground">
                    Materials/Labor: {formatMoney(computeLineSubtotal(line))}
                  </span>
                  <span className="text-sm font-semibold">
                    Line Total: {formatMoney(computeLineTotal(line))}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Rolled-up totals + commission slider */}
      <div className="border-t border-border pt-4 space-y-3">
        <div className="flex items-start justify-between gap-4 text-sm">
          <span className="text-muted-foreground">Roof Quote</span>
          <span className="font-medium text-right break-words">{formatMoney(baseQuoteTotal)}</span>
        </div>
        <div className="flex items-start justify-between gap-4 text-sm">
          <span className="text-muted-foreground">Additional Trades Subtotal</span>
          <span className="font-medium text-right break-words">{formatMoney(tradesSubtotal)}</span>
        </div>

        <div className="space-y-2 pt-2">
          <div className="flex items-start justify-between gap-4">
            <Label className="text-sm">Sales Markup / Commission (whole job)</Label>
            <span className="text-sm font-semibold text-right">
              {formatMoney(commissionDollars)}
            </span>
          </div>
          <Slider
            value={[commissionDollars]}
            onValueChange={(v) => setCommissionDollars(v[0])}
            min={0}
            max={25000}
            step={250}
            className="w-full"
          />
          <p className="text-[11px] text-muted-foreground">
            $0–$25,000 in $250 steps. Added on top of the combined quote.
          </p>
        </div>

        <div className="flex items-end justify-between gap-4 pt-3 border-t border-border">
          <span className="text-base font-semibold">Grand Total</span>
          <span className="text-right text-2xl font-bold text-primary break-words">
            {formatMoney(grandTotal)}
          </span>
        </div>
      </div>
    </div>
  );
}
